/**
 * Session persistence.
 *
 * sessionStorage, NOT localStorage. localStorage is shared by every tab on the
 * origin, so an agent tab and a supervisor tab would collapse into a single
 * identity — which destroys the two-tab supervisor demo and, worse, would let
 * one role silently inherit the other's capabilities in the UI.
 *
 * Every access is guarded: storage THROWS outright in private modes and where
 * cookies are blocked. Persistence is a convenience, so it degrades to
 * "sign in each time" rather than breaking sign-in itself.
 */

const KEY = "savvy.session.v1";

export interface StoredSession {
  accessToken: string;
  refreshToken: string;
  /** Epoch ms. */
  expiresAt: number;
  username: string;
  caps: string[];
}

export function loadSession(): StoredSession | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const s = JSON.parse(raw) as Partial<StoredSession>;
    if (!s.accessToken || !s.refreshToken) return null;
    return {
      accessToken: s.accessToken,
      refreshToken: s.refreshToken,
      expiresAt: s.expiresAt ?? 0,
      username: s.username ?? "",
      caps: s.caps ?? [],
    };
  } catch {
    clearSession();
    return null;
  }
}

export function saveSession(s: StoredSession): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* no persistence available; the session lives in memory only */
  }
}

export function clearSession(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}
